import React, { useState, useMemo } from 'react';
import _ from 'lodash';
import { Divider, List, ListItem, IconButton, Box, Flex, Search, Icon } from '@vibe/core';
import { Board as BoardIcon, Workspace, Add as AddIcon } from '@vibe/icons';
import fuzzysort from 'fuzzysort';
import { BoardBasic } from '@gorilla/common/src/lib/monday-api/api';
import { useAllBoards } from '../hooks/use-boards';

type BoardResultProps = {
  board: BoardBasic;
  onBoardAdd: Function;
  title: any;
};

function BoardResult({ board, onBoardAdd, title }: BoardResultProps) {
  return (
    <ListItem
      size="large"
      className="list-item-board-selection"
      onClick={() => {
        onBoardAdd();
      }}
    >
      <div style={{ position: 'relative', width: '100%' }}>
        <Flex align="start" justify="start" direction="column" gap={2} wrap={true}>
          <Flex>
            <div
              style={{
                flex: 1,
                marginLeft: '0px',
              }}
            >
              <Flex gap={6}>
                <Icon icon={BoardIcon} />
                <div
                  style={{
                    overflow: 'hidden',
                    maxWidth: '350px',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <strong>{title}</strong>
                </div>
              </Flex>
            </div>
          </Flex>
          <Flex gap={6} style={{ opacity: 0.5 }}>
            <Icon icon={Workspace} />
            <div
              style={{
                overflow: 'hidden',
                maxWidth: '300px',
                textOverflow: 'ellipsis',
              }}
            >
              {board?.workspace ? board?.workspace.name : 'Main Workspace'}
            </div>
          </Flex>
        </Flex>
        <div
          style={{
            position: 'absolute',
            top: '50%',
            right: 0,
            transform: 'translateY(-50%)',
          }}
        >
          <IconButton
            icon={AddIcon}
            kind="primary"
            size="xs"
            onClick={() => {
              onBoardAdd();
            }}
          />
        </div>
      </div>
    </ListItem>
  );
}

type BoardPickerProps = {
  onBoardSelect: (selectedBoardId: number) => void;
};

export function SingleBoardPicker({ onBoardSelect }: BoardPickerProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const { boards, isLoading, error } = useAllBoards();

  const results = useMemo(() => {
    if (!boards) {
      return [];
    }

    if (!searchTerm) {
      // TODO: return some default boards?
      return [];
    }

    let filteredBoards = boards;

    const searchResults = fuzzysort.go(searchTerm, filteredBoards, {
      keys: ['name'],
    });

    return searchResults;
  }, [searchTerm, boards]);

  const recentlyUsedBoards = useMemo(() => {
    if (!boards) {
      return [];
    }

    return boards;
  }, [boards]);

  if (error) {
    return (
      <div>
        <strong>Failed to load boards</strong>
      </div>
    );
  }

  if (isLoading || !boards) {
    return (
      <div>
        <strong>Loading boards...</strong>
      </div>
    );
  }

  let resultsContent: React.ReactNode = null;

  if (!searchTerm) {
    resultsContent = (
      <Box>
        <List>
          <>
            {recentlyUsedBoards.slice(0, 25).map((board, index) => {
              return (
                <>
                  <BoardResult
                    key={board.id}
                    title={board.name}
                    board={board}
                    onBoardAdd={() => {
                      onBoardSelect(board.id);
                    }}
                  />
                  {index !== recentlyUsedBoards.slice(0, 25).length - 1 && <Divider withoutMargin />}
                </>
              );
            })}
          </>
        </List>
      </Box>
    );
  } else if (!results.length) {
    resultsContent = (
      <>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
            width: '100%',
            height: '100%',
          }}
        >
          <div style={{ pointerEvents: 'none', fontWeight: 300, textAlign: 'center', color: '#afb0bd' }}>
            <Icon icon={BoardIcon} size={20} />
            <br />
            No boards found
          </div>
        </div>
      </>
    );
  } else {
    resultsContent = (
      <Box>
        <List>
          <>
            {results.slice(0, 25).map((result, index) => {
              let title: any = <strong>could not load result</strong>;

              try {
                title = result[0].highlight((m, i) => <strong key={i}>{m}</strong>);
              } catch (err) {
                console.log('err', err);
              }

              return (
                <>
                  <BoardResult
                    key={result.obj.id}
                    title={title}
                    board={result.obj}
                    onBoardAdd={() => {
                      onBoardSelect(result.obj.id);
                    }}
                  />
                  {index !== recentlyUsedBoards.slice(0, 25).length - 1 && <Divider withoutMargin />}
                </>
              );
            })}
          </>
        </List>
      </Box>
    );
  }

  return (
    <>
      <div
        style={{
          overflow: 'hidden',
          border: '1px solid #d0d4e4',
          borderRadius: '8px',
        }}
      >
        <Flex gap={0} align="stretch">
          <div
            style={{
              width: 'calc(100%)',
            }}
          >
            {/* height: '374px' */}
            <div style={{ height: 'calc(90vh - 160px)' }}>
              <div
                style={{
                  padding: '12px',
                  backgroundColor: '#f6f7fb',
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                }}
              >
                <Search
                  placeholder='Search for "Board Name" or "Subitems of Board"'
                  value={searchTerm}
                  onChange={(newSearchTerm) => {
                    setSearchTerm(newSearchTerm);
                  }}
                  autoFocus
                  size="small"
                />
              </div>
              <Divider withoutMargin={true} />
              <div style={{ overflowY: 'auto', height: 'calc(90vh - 225px)' }}>{resultsContent}</div>
            </div>
          </div>
        </Flex>
      </div>
    </>
  );
}
